import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from './components/StatsCard';
import MembersList from './components/MembersList';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import CreateMemberModal from './components/CreateMemberModal';
import CreateStaffModal from './components/CreateStaffModal';





const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalClasses: 0,
    revenue: 0,
    upcomingEvents: 0,
    totalEvents: 0
  });
  const [members, setMembers] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allTenants, setAllTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showCreateMemberModal, setShowCreateMemberModal] = useState(false);
  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/login');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isSuperAdmin) {
      loadAllTenants();
    } else if (currentTenant?.id) {
      loadDashboardData(currentTenant?.id);
    }
  }, [currentTenant, isSuperAdmin]);

  const loadAllTenants = async () => {
    try {
      const { data: tenantsData, error: tenantsError } = await supabase
        ?.from('tenants')
        ?.select('*')
        ?.order('name', { ascending: true });

      if (tenantsError) throw tenantsError;
      setAllTenants(tenantsData || []);
      
      // Load aggregated data for all tenants
      if (tenantsData?.length > 0) {
        loadDashboardData(null); // null means load all tenants
      }
    } catch (err) {
      setError(err?.message || 'Failed to load tenants');
    }
  };

  const loadDashboardData = async (tenantId = null) => {
    setLoading(true);
    setError('');

    try {
      // Build query based on whether we're filtering by tenant
      const buildQuery = (table) => {
        let query = supabase?.from(table)?.select('*');
        if (tenantId) {
          query = query?.eq('tenant_id', tenantId);
        }
        return query;
      };

      // Load members
      let membersQuery = supabase?.from('tenant_members')?.select('*, user_profiles(*)');
      if (tenantId) {
        membersQuery = membersQuery?.eq('tenant_id', tenantId);
      }
      const { data: membersData, error: membersError } = await membersQuery?.order('joined_at', { ascending: false });

      if (membersError) throw membersError;
      setMembers(membersData || []);

      // Load classes
      const { data: classesData, error: classesError } = await buildQuery('classes');

      if (classesError) throw classesError;

      // Load events
      const { data: eventsData, error: eventsError } = await buildQuery('events');

      if (eventsError) throw eventsError;

      const upcomingEvents = eventsData?.filter(e => new Date(e?.start_time) >= new Date())?.length || 0;

      // Load recent workouts for activity
      let workoutsQuery = supabase?.from('workouts')?.select('*, user_profiles(*)');
      if (tenantId) {
        workoutsQuery = workoutsQuery?.eq('tenant_id', tenantId);
      }
      const { data: workoutsData, error: workoutsError } = await workoutsQuery?.order('created_at', { ascending: false })?.limit(10);

      if (workoutsError) throw workoutsError;
      setRecentActivities(workoutsData || []);

      // Calculate stats
      const activeMembers = membersData?.filter(m => m?.is_active)?.length || 0;
      const totalRevenue = membersData?.reduce((sum, m) => {
        const planPrices = { basic: 29, premium: 59, elite: 99 };
        return sum + (planPrices?.[m?.membership_plan] || 0);
      }, 0);

      setStats({
        totalMembers: membersData?.length || 0,
        activeMembers,
        totalClasses: classesData?.length || 0,
        revenue: totalRevenue,
        upcomingEvents,
        totalEvents: eventsData?.length || 0
      });
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = (tenantId) => {
    if (tenantId === 'all') {
      setSelectedTenant(null);
      loadDashboardData(null);
    } else {
      const tenant = allTenants?.find(t => t?.id === tenantId);
      setSelectedTenant(tenant);
      loadDashboardData(tenantId);
    }
  };

  const handleInviteMember = () => {
    // Navigate to invite member page or open modal
    console.log('Invite member');
  };

  const handleCreateClass = () => {
    navigate('/class-schedule');
  };

  const handleViewReports = () => {
    console.log('View reports');
  };

  const handleManageSettings = () => {
    console.log('Manage settings');
  };

  const handleCreateEvent = () => {
    console.log('Create event');
  };

  const handleCreateMember = () => {
    setShowCreateMemberModal(true);
  };

  const handleCreateStaff = () => {
    setShowCreateStaffModal(true);
  };

  const handleUserCreated = () => {
    setShowCreateMemberModal(false);
    setShowCreateStaffModal(false);
    loadDashboardData(selectedTenant?.id || currentTenant?.id);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
            <p className="text-destructive text-lg font-semibold mb-2">Error Loading Dashboard</p>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => isSuperAdmin ? loadAllTenants() : loadDashboardData(currentTenant?.id)} variant="default">
              <Icon name="RefreshCw" size={18} className="mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const displayTenant = isSuperAdmin ? (selectedTenant || { name: 'All Gym Locations' }) : currentTenant;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8 pb-24 md:pb-8">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              {isSuperAdmin && (
                <div className="mb-3">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                    <Icon name="Shield" size={16} />
                    Master Administrator
                  </span>
                </div>
              )}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                {displayTenant?.name} Dashboard
              </h1>
              <p className="text-muted-foreground">
                {isSuperAdmin ? 'Cross-tenant administrative access' : 'Manage your gym, members, and operations'}
              </p>
            </div>
            <div className="flex gap-3">
              {isSuperAdmin && (
                <select
                  value={selectedTenant?.id || 'all'}
                  onChange={(e) => handleTenantChange(e?.target?.value)}
                  className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Locations</option>
                  {allTenants?.map(tenant => (
                    <option key={tenant?.id} value={tenant?.id}>
                      {tenant?.name}
                    </option>
                  ))}
                </select>
              )}
              <Button onClick={handleInviteMember} variant="default" size="lg">
                <Icon name="UserPlus" size={20} className="mr-2" />
                Invite Member
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <StatsCard
            icon="Users"
            iconColor="var(--color-primary)"
            title="Total Members"
            value={stats?.totalMembers}
            subtitle={`${stats?.activeMembers} active`}
            trend="up"
            trendValue="+12%"
          />
          <StatsCard
            icon="Calendar"
            iconColor="var(--color-accent)"
            title="Active Classes"
            value={stats?.totalClasses}
            subtitle="This month"
            trend="up"
            trendValue="+3"
          />
          <StatsCard
            icon="DollarSign"
            iconColor="var(--color-success)"
            title="Monthly Revenue"
            value={`$${stats?.revenue?.toLocaleString()}`}
            subtitle="From memberships"
            trend="up"
            trendValue="+8%"
          />
          <StatsCard
            icon="TrendingUp"
            iconColor="var(--color-warning)"
            title="Growth Rate"
            value="15%"
            subtitle="Last 30 days"
            trend="up"
            trendValue="+2%"
          />
        </div>

        {/* Quick Actions */}
        <QuickActions
          onInviteMember={handleInviteMember}
          onCreateClass={handleCreateClass}
          onCreateEvent={handleCreateEvent}
          onViewReports={handleViewReports}
          onManageSettings={handleManageSettings}
          onCreateMember={handleCreateMember}
          onCreateStaff={handleCreateStaff}
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Members List */}
          <div className="lg:col-span-2">
            <MembersList members={members} onRefresh={() => isSuperAdmin ? loadAllTenants() : loadDashboardData(currentTenant?.id)} />
          </div>

          {/* Recent Activities */}
          <div className="lg:col-span-1">
            <RecentActivity activities={recentActivities} />
          </div>
        </div>
      </main>

      {/* Create Member Modal */}
      {showCreateMemberModal && (
        <CreateMemberModal
          onClose={() => setShowCreateMemberModal(false)}
          onUserCreated={handleUserCreated}
          tenantId={selectedTenant?.id || currentTenant?.id}
        />
      )}

      {/* Create Staff Modal */}
      {showCreateStaffModal && (
        <CreateStaffModal
          onClose={() => setShowCreateStaffModal(false)}
          onUserCreated={handleUserCreated}
          tenantId={selectedTenant?.id || currentTenant?.id}
        />
      )}
      <MobileBottomNav />
    </div>
  );
};

export default AdminDashboard;